import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { getStatusChipStyles, getStatusLabel } from '../../../utils/jobStatuses';
import { getTechnicianDisplayName, panelSurfaceSx } from './controlCenterUtils';
import ControlCenterMap from './ControlCenterMap';
import ControlCenterInfoTooltip from './ControlCenterInfoTooltip';

/** User-facing explanation for Jobs Status chips + list (aligned with controlCenterDao). */
export const JOBS_STATUS_INFO =
  'Shows pipeline jobs for the selected clients (or all clients). Counts and the list are not limited by the date range — only by client. Includes statuses from Open through Job Done, plus Ready for Invoicing. Cancelled, Invoiced, Invoiced/Loss, and Recall No Charge are not in this set. The list is ordered by most recently updated first (up to 500 jobs).';

/** Shared height for list / map / detail columns on md+ so a long detail pane cannot stretch the row. */
const PANEL_BODY_HEIGHT = 480;
const PANEL_BODY_HEIGHT_MOBILE = 360;

const formatAppointmentDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatAppointmentTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const StatusChip = ({ statusId, label, count, selected, onClick }) => {
  const styles = getStatusChipStyles(statusId);
  const accent = styles?.borderColor || '#94a3b8';

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 100,
        height: 40,
        px: 0.75,
        py: 0.5,
        borderRadius: '5px',
        border: '1px solid',
        borderColor: selected ? accent : 'divider',
        bgcolor: selected ? `${accent}14` : 'background.paper',
        cursor: 'pointer',
        textAlign: 'left',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: 5, alignSelf: 'stretch', borderRadius: '2px', bgcolor: accent }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>
          {count}
        </Typography>
      </Box>
    </Box>
  );
};

const JobListItem = ({ job, selected, onClick, isDark }) => {
  const styles = getStatusChipStyles(job.status);
  const tech = job.technicians?.[0];
  const techName = getTechnicianDisplayName(tech);
  const appointment = job.appointments?.[0]?.scheduledDate;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? (styles?.borderColor || 'primary.main') : (isDark ? '#334155' : '#e7e7e7'),
        bgcolor: selected ? (isDark ? '#1e293b' : '#f8fafc') : 'transparent',
        cursor: 'pointer',
        mb: 1,
        '&:hover': {
          bgcolor: isDark ? '#1e293b' : '#f8fafc',
        },
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {job.work_order
          ? `${job.work_order} | ${getStatusLabel(job.status)}`
          : getStatusLabel(job.status)}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
            {job.clientName || 'Unknown client'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
            {appointment ? formatAppointmentTime(appointment) : 'Unscheduled'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
            {[job.address, job.city, job.state].filter(Boolean).join(', ') || 'No address'}
          </Typography>
        </Box>
        {techName && (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, ml: 'auto', mb: 0.5 }}>
              {techName.charAt(0)}
            </Avatar>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', maxWidth: 72 }} noWrap>
              {techName}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const JobDetailPanel = ({ job, onViewFullJob, isDark }) => {
  const panelSx = {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    borderLeft: { lg: '1px solid' },
    borderColor: isDark ? '#334155' : '#e7e7e7',
    bgcolor: isDark ? '#0f172a' : '#fafafa',
    overflow: 'hidden',
  };

  if (!job) {
    return (
      <Box
        sx={{
          ...panelSx,
          p: 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Select a job to view details
        </Typography>
      </Box>
    );
  }

  const tech = job.technicians?.[0];
  const techName = getTechnicianDisplayName(tech);

  return (
    <Box sx={panelSx}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {job.work_order
            ? `${job.work_order} | ${getStatusLabel(job.status)}`
            : getStatusLabel(job.status)}
        </Typography>

        <Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Client</Typography>
          <Typography sx={{ fontSize: 13 }}>{job.clientName || '—'}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Address</Typography>
          <Typography sx={{ fontSize: 13 }}>
            {[job.address, job.city, job.state, job.zip_code].filter(Boolean).join(', ') || '—'}
          </Typography>
        </Box>
        {techName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{techName.charAt(0)}</Avatar>
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Assigned Tech</Typography>
              <Typography sx={{ fontSize: 13 }}>{techName}</Typography>
            </Box>
          </Box>
        )}

        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Job Description</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {job.description || 'No description'}
          </Typography>
        </Box>

        {(job.appointments || []).length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.75 }}>Appointment</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr 1fr',
                gap: 0.5,
                fontSize: 12,
              }}
            >
              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>#</Typography>
              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Date</Typography>
              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Time</Typography>
              {job.appointments.map((appt) => (
                <React.Fragment key={`${appt.technicianId}-${appt.scheduledDate}`}>
                  <Typography>{appt.index}</Typography>
                  <Typography>{formatAppointmentDate(appt.scheduledDate)}</Typography>
                  <Typography>{formatAppointmentTime(appt.scheduledDate)}</Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          pt: 1,
          pb: 2,
          borderTop: '1px solid',
          borderColor: isDark ? '#334155' : '#e7e7e7',
          bgcolor: isDark ? '#0f172a' : '#fafafa',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={() => onViewFullJob?.(job)}
          sx={{
            backgroundColor: isDark ? '#fff' : '#000',
            color: isDark ? '#000' : '#fff',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            px: 3,
            py: 1.25,
            borderRadius: 1.5,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDark ? '#e0e0e0' : '#333',
              boxShadow: 'none',
            },
          }}
        >
          View full Job
        </Button>
      </Box>
    </Box>
  );
};

export default function JobsStatusPanel({
  statusCounts = [],
  jobs = [],
  jobsTotal = null,
  loading,
  error,
  selectedStatus,
  onStatusChange,
  onViewFullJob,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [mobileView, setMobileView] = useState('list');

  const totalJobs = useMemo(
    () => statusCounts.reduce((sum, item) => sum + (item.count || 0), 0),
    [statusCounts],
  );

  const filteredJobs = useMemo(() => {
    if (selectedStatus == null) return jobs;
    return jobs.filter((job) => Number(job.status) === Number(selectedStatus));
  }, [jobs, selectedStatus]);

  const selectedJob = useMemo(() => {
    if (!filteredJobs.length) return null;
    return filteredJobs.find((j) => j.id === selectedJobId) || filteredJobs[0];
  }, [filteredJobs, selectedJobId]);

  const handleSelectJob = (job) => {
    setSelectedJobId(job.id);
    if (!isMdUp) setMobileView('detail');
  };

  return (
    <Box sx={panelSurfaceSx(theme)}>
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 500,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Jobs Status
            <ControlCenterInfoTooltip title={JOBS_STATUS_INFO} />
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {totalJobs} jobs
          </Typography>
        </Box>

        {jobsTotal != null && jobsTotal > jobs.length && (
          <Typography sx={{ fontSize: 12, color: 'warning.main', mb: 1 }}>
            Showing {jobs.length} of {jobsTotal} jobs — narrow your filters to see the rest.
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {statusCounts.map((item) => (
            <StatusChip
              key={item.statusId}
              statusId={item.statusId}
              label={item.label}
              count={item.count}
              selected={Number(selectedStatus) === Number(item.statusId)}
              onClick={() =>
                onStatusChange?.(
                  Number(selectedStatus) === Number(item.statusId) ? null : item.statusId,
                )
              }
            />
          ))}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <>
          {!isMdUp && (
            <Box sx={{ px: 1.5, pb: 1 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mobileView}
                onChange={(_, value) => value && setMobileView(value)}
                fullWidth
              >
                <ToggleButton value="list">List</ToggleButton>
                <ToggleButton value="map">Map</ToggleButton>
                <ToggleButton value="detail">Detail</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '280px 1fr',
                lg: '280px minmax(0, 1fr) 300px',
              },
              // lg: one fixed row so list/map/detail stay aligned; md: list+map row fixed, detail below
              gridTemplateRows: {
                xs: 'auto',
                md: `${PANEL_BODY_HEIGHT}px auto`,
                lg: `${PANEL_BODY_HEIGHT}px`,
              },
              height: { lg: PANEL_BODY_HEIGHT },
              overflow: { lg: 'hidden' },
              borderTop: '1px solid',
              borderColor: isDark ? '#334155' : '#e7e7e7',
            }}
          >
            {(isMdUp || mobileView === 'list') && (
              <Box
                sx={{
                  p: 1.5,
                  height: { xs: PANEL_BODY_HEIGHT_MOBILE, md: '100%' },
                  minHeight: 0,
                  overflow: 'auto',
                  borderRight: { md: '1px solid' },
                  borderColor: isDark ? '#334155' : '#e7e7e7',
                }}
              >
                {filteredJobs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No jobs in this status
                  </Typography>
                ) : (
                  filteredJobs.map((job) => (
                    <JobListItem
                      key={job.id}
                      job={job}
                      selected={selectedJob?.id === job.id}
                      onClick={() => handleSelectJob(job)}
                      isDark={isDark}
                    />
                  ))
                )}
              </Box>
            )}

            {(isMdUp || mobileView === 'map') && (
              <Box sx={{ height: { xs: 300, md: '100%' }, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
                <ControlCenterMap
                  jobs={filteredJobs}
                  selectedJobId={selectedJob?.id}
                  onSelectJob={handleSelectJob}
                  height={isMdUp ? PANEL_BODY_HEIGHT : 300}
                />
              </Box>
            )}

            {isLgUp && (
              <JobDetailPanel
                job={selectedJob}
                onViewFullJob={onViewFullJob}
                isDark={isDark}
              />
            )}

            {!isMdUp && mobileView === 'detail' && (
              <Box sx={{ height: PANEL_BODY_HEIGHT_MOBILE, minHeight: 0 }}>
                <JobDetailPanel
                  job={selectedJob}
                  onViewFullJob={onViewFullJob}
                  isDark={isDark}
                />
              </Box>
            )}

            {isMdUp && !isLgUp && (
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  borderTop: '1px solid',
                  borderColor: isDark ? '#334155' : '#e7e7e7',
                  height: 320,
                  minHeight: 0,
                }}
              >
                <JobDetailPanel
                  job={selectedJob}
                  onViewFullJob={onViewFullJob}
                  isDark={isDark}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
